import { useState } from 'preact/hooks'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { List, Pencil, Plus, Trash2, Save, X, GripVertical, Info } from 'lucide-preact'
import type { Control, FieldValues, Path, UseFieldArrayReturn } from 'react-hook-form'
import { TextBoxField } from '@/components'
import { BRAND_CLASSES } from '@/constants/theme'

// react-hook-form passthrough for the field-array props: this section is intentionally
// form-shape-agnostic; the concrete form type is supplied by the parent panel (OnlineEventPanel,
// etc.). A bare `any` here is deliberate and preserves caller-side typing (FieldValues is invariant
// and rejects specific forms).
//
// `control` cannot use it. Since react-hook-form 7.83 `Control` carries a `_options.validate` whose
// field-name union resolves through the form type, which makes Control invariant in *both*
// directions — `Control<any>` stopped accepting a concrete form too. Hence the type parameter.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyForm = any

interface AgendaSectionProps<T extends FieldValues> {
  editing: boolean
  saving: boolean
  editDisabled?: boolean
  /**
   * The web is bound to a conference, so the agenda is owned by BigMarker for good. Distinct from
   * `editDisabled`, which also covers the temporary reasons (syncing, another section open, signed
   * off): those get a greyed-out button because they come back, this one gets no button at all.
   */
  externallyManaged?: boolean
  hasBigMarker: boolean
  /** Display name of the connected BigMarker integration, used in the one-way sync notice */
  integrationName?: string
  /** The normalised initial agenda items from server data (trimmed, non-empty) */
  initialItems: string[]
  agendaChanged: boolean
  hasDirtyEmpty: boolean
  agendaFields: UseFieldArrayReturn<AnyForm, 'agenda', 'id'>['fields']
  control: Control<T>
  append: UseFieldArrayReturn<AnyForm, 'agenda', 'id'>['append']
  remove: UseFieldArrayReturn<AnyForm, 'agenda', 'id'>['remove']
  move: UseFieldArrayReturn<AnyForm, 'agenda', 'id'>['move']
  onEdit: () => void
  onSave: () => void
  onCancel: () => void
}

export function AgendaSection<T extends FieldValues>({
  editing,
  saving,
  editDisabled,
  externallyManaged,
  hasBigMarker,
  integrationName = 'BigMarker',
  initialItems,
  agendaChanged,
  hasDirtyEmpty,
  agendaFields,
  control,
  append,
  remove,
  move,
  onEdit,
  onSave,
  onCancel,
}: AgendaSectionProps<T>) {
  const { canEdit } = useWorkspaceCapabilities()
  const [agendaDragIndex, setAgendaDragIndex] = useState<number | null>(null)
  const [agendaDragOverIndex, setAgendaDragOverIndex] = useState<number | null>(null)

  // Built from the row index at render time, so `Path<T>` cannot express it for a generic T. Named
  // here rather than cast inline, to keep the type assertion out of the markup below.
  const agendaFieldName = (idx: number) => `agenda.${idx}.value` as Path<T>

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <List className="w-5 h-5 text-primary-600" /> Agenda
        </h2>
        {!editing ? (
          canEdit && !externallyManaged && <button type="button" disabled={editDisabled} onClick={onEdit} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"><Pencil className="w-4 h-4" /> Edit</button>
        ) : (
          <div className="flex items-center gap-2">
            <button type="button" onClick={onSave} disabled={saving || hasDirtyEmpty || !agendaChanged} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
            <button type="button" onClick={onCancel} disabled={saving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"><X className="w-4 h-4" /> Cancel</button>
          </div>
        )}
      </div>
      {/* The sync is one-way (BigMarker -> OrbitalX) and the agenda is locked here the moment the web
          is pushed, which reopening a signed-off web does not undo. That was a tooltip nobody read. */}
      {hasBigMarker && (
        <div className={`mt-4 flex items-start gap-2 p-3 rounded-lg ${BRAND_CLASSES.STATUS.INFO}`}>
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">Agenda is managed in {integrationName}</p>
            <p className="mt-1">
              Agenda items can only be changed in {integrationName} &mdash; edits there sync back here
              automatically.
            </p>
          </div>
        </div>
      )}
      {!editing ? (
        <div className="mt-4 space-y-2">
          {initialItems.length > 0 ? initialItems.map((item, i) => (
            <div key={i} className="flex items-start gap-3"><span className="flex-shrink-0 text-xs mt-1 w-5 h-5 flex items-center justify-center rounded-full bg-primary-600 text-white font-medium">{i + 1}</span><span className="text-sm text-slate-700 dark:text-slate-300 break-words">{item}</span></div>
          )) : <p className="text-slate-500 dark:text-slate-400 text-sm">No agenda items yet.</p>}
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {agendaFields.map((field, idx) => {
            const isDragOver = agendaDragOverIndex === idx && agendaDragIndex !== null && agendaDragIndex !== idx
            return (
              <div key={field.id} className={`flex items-start gap-2 p-2 rounded-md border transition-colors ${isDragOver ? 'border-primary-400 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20' : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900'}`} draggable onDragStart={(e) => { setAgendaDragIndex(idx); if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move' } }} onDragOver={(e) => { e.preventDefault(); if (agendaDragOverIndex !== idx) setAgendaDragOverIndex(idx) }} onDragLeave={() => { if (agendaDragOverIndex === idx) setAgendaDragOverIndex(null) }} onDrop={(e) => { e.preventDefault(); if (agendaDragIndex !== null && agendaDragIndex !== idx) move(agendaDragIndex, idx); setAgendaDragIndex(null); setAgendaDragOverIndex(null) }} onDragEnd={() => { setAgendaDragIndex(null); setAgendaDragOverIndex(null) }}>
                    <div className="flex items-center gap-1 pr-1 select-none">
                      <button type="button" className="cursor-grab p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500" tabIndex={0} aria-label={`Reorder agenda item ${idx + 1}. Use Arrow Up / Arrow Down to move.`} onKeyDown={(e) => { if (e.key === 'ArrowUp' && idx > 0) { e.preventDefault(); move(idx, idx - 1) } else if (e.key === 'ArrowDown' && idx < agendaFields.length - 1) { e.preventDefault(); move(idx, idx + 1) } }}><GripVertical className="w-5 h-5" /></button>
                      <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 w-4 text-center">{idx + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <TextBoxField
                          control={control}
                          name={agendaFieldName(idx)}
                          containerClassName="flex-1"
                          maxLength={200}
                          showCharCounter
                          placeholder="Agenda item"
                          rules={{ validate: v => (v ?? '').trim().length > 0 || 'Required' }}
                        />
                        <button type="button" onClick={() => remove(idx)} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 self-start mt-2" aria-label="Delete agenda item"><Trash2 className="w-3 h-3" /> Delete</button>
                      </div>
                    </div>
              </div>
            )
          })}
          <button type="button" onClick={() => append({ value: '' })} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"><Plus className="w-4 h-4" /> Add item</button>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">Drag items or focus a handle and press Arrow Up/Down to reorder.</p>
        </div>
      )}
    </div>
  )
}
